import * as React from "react";
import {
  Filter,
  Group,
  ArrowUpDown,
  Search,
  Columns3,
  Check,
  X,
  ChevronDown,
  ArrowUp,
  ArrowDown,
} from "lucide-react";

import { cn } from "@/lib/cn";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

/**
 * HeroToolbar
 *
 * Canonical reference: ianfiles/ui-design-system.md §3.2 + §3.5
 *
 * The list-page toolbar that owns Filter By / Group By / Sort By / Search /
 * Columns. Replaces the legacy `<FilterBar>` + `<CollapsibleSearch>` +
 * `<ColumnVisibilityMenu>` trio.
 *
 * Build a memoized `heroColumns` array describing what's filterable /
 * groupable / sortable / searchable, and hand it to BOTH:
 *   - `<HeroToolbar columns={heroColumns} ... />`
 *   - `useHeroSortedRows(rows, heroColumns)` (from `./hero-filter-bridge`)
 *
 * The toolbar manages its own internal chip selection state for client-side
 * `clientFilter: true` columns. For server-side filters (`clientFilter:
 * false`), use `useChipFilterParams(config)` in `./hero-filter-bridge` to
 * read the same chip state as query params.
 *
 * Column shape:
 *
 *   interface HeroToolbarColumn {
 *     key: string;                 // stable id
 *     label: string;               // user-visible chip label
 *     filterable?: boolean;        // appears in "Filter By"
 *     groupable?: boolean;         // appears in "Group By"
 *     sortable?: boolean;          // appears in "Sort By"
 *     multi?: boolean;             // multi-select chip
 *     options?: { value: string; label: string }[];
 *     clientFilter?: boolean;      // default true; false → server-driven
 *     hidden?: boolean;            // skip in all menus
 *   }
 *
 * Only **list pages** should use this toolbar. Detail pages and bespoke
 * panels (Inventory tabs) keep their own filter UI per §3.2 rule #4.
 */

export interface HeroToolbarOption {
  value: string;
  label: string;
}

export interface HeroToolbarColumn {
  key: string;
  label: string;
  filterable?: boolean;
  groupable?: boolean;
  sortable?: boolean;
  /** Allow multiple selected values for this filter. Defaults to false. */
  multi?: boolean;
  /** Filter option list. Required when `filterable` is true. */
  options?: HeroToolbarOption[];
  /** Whether the filter is applied client-side (default true) or server-side. */
  clientFilter?: boolean;
  /** Hide the column entirely from every menu — used to short-circuit a config. */
  hidden?: boolean;
}

/** Internal toolbar state — value(s) selected per filterable column. */
export type HeroFilterState = Record<string, string[]>;

/** Sort selection. */
export interface HeroSortState {
  columnKey: string;
  direction: "asc" | "desc";
}

/** Group selection. */
export type HeroGroupState = string | null;

export interface HeroToolbarProps {
  /** Column definitions driving the chip menus. */
  columns: HeroToolbarColumn[];

  /** Current search value (controlled). */
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  searchPlaceholder?: string;

  /** Current filter chips (controlled). */
  filters?: HeroFilterState;
  onFiltersChange?: (next: HeroFilterState) => void;

  /** Current sort selection (controlled). */
  sort?: HeroSortState | null;
  onSortChange?: (next: HeroSortState | null) => void;

  /** Current group selection (controlled). */
  group?: HeroGroupState;
  onGroupChange?: (next: HeroGroupState) => void;

  /**
   * When set, the toolbar exposes a Columns menu using these DataTable columns.
   * Visibility is persisted in `localStorage[<tableKey>]`.
   */
  tableKey?: string;
  tableColumns?: Array<{ key: string; header: string; hideable?: boolean }>;
  onColumnVisibilityChange?: (next: Record<string, boolean>) => void;
  columnVisibility?: Record<string, boolean>;

  className?: string;
}

export function HeroToolbar({
  columns,
  searchValue = "",
  onSearchChange,
  searchPlaceholder = "Search…",
  filters = {},
  onFiltersChange,
  sort = null,
  onSortChange,
  group = null,
  onGroupChange,
  tableKey,
  tableColumns,
  columnVisibility,
  onColumnVisibilityChange,
  className,
}: HeroToolbarProps) {
  const visibleColumns = React.useMemo(
    () => columns.filter((c) => !c.hidden),
    [columns]
  );

  const filterableColumns = visibleColumns.filter(
    (c) => c.filterable && c.options && c.options.length > 0
  );
  const groupableColumns = visibleColumns.filter((c) => c.groupable);
  const sortableColumns = visibleColumns.filter((c) => c.sortable);

  // Flatten active filter chips so each one renders inline with an X
  const activeChips: Array<{
    columnKey: string;
    columnLabel: string;
    value: string;
    optionLabel: string;
  }> = [];
  for (const col of filterableColumns) {
    const selected = filters[col.key] ?? [];
    for (const value of selected) {
      const option = col.options?.find((o) => o.value === value);
      activeChips.push({
        columnKey: col.key,
        columnLabel: col.label,
        value,
        optionLabel: option?.label ?? value,
      });
    }
  }

  const handleToggleFilter = (col: HeroToolbarColumn, value: string) => {
    const current = filters[col.key] ?? [];
    let nextValues: string[];
    if (col.multi) {
      nextValues = current.includes(value)
        ? current.filter((v) => v !== value)
        : [...current, value];
    } else {
      // Single-select toggles off on second click
      nextValues = current.includes(value) ? [] : [value];
    }
    const next: HeroFilterState = { ...filters };
    if (nextValues.length === 0) delete next[col.key];
    else next[col.key] = nextValues;
    onFiltersChange?.(next);
  };

  const handleRemoveChip = (columnKey: string, value: string) => {
    const current = filters[columnKey] ?? [];
    const nextValues = current.filter((v) => v !== value);
    const next: HeroFilterState = { ...filters };
    if (nextValues.length === 0) delete next[columnKey];
    else next[columnKey] = nextValues;
    onFiltersChange?.(next);
  };

  const handleSortPick = (columnKey: string) => {
    if (sort?.columnKey === columnKey) {
      onSortChange?.({
        columnKey,
        direction: sort.direction === "asc" ? "desc" : "asc",
      });
    } else {
      onSortChange?.({ columnKey, direction: "asc" });
    }
  };

  return (
    <div className={cn("flex flex-col gap-2", className)}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        {/* Left cluster: Filter By / Group By / Sort By */}
        <div className="flex items-center gap-1.5 flex-wrap">
          {filterableColumns.length > 0 ? (
            <FilterByMenu
              columns={filterableColumns}
              filters={filters}
              onToggle={handleToggleFilter}
            />
          ) : null}

          {groupableColumns.length > 0 ? (
            <GroupByMenu
              columns={groupableColumns}
              group={group}
              onChange={(next) => onGroupChange?.(next)}
            />
          ) : null}

          {sortableColumns.length > 0 ? (
            <SortByMenu
              columns={sortableColumns}
              sort={sort}
              onPick={handleSortPick}
              onClear={() => onSortChange?.(null)}
            />
          ) : null}
        </div>

        {/* Right cluster: Search + Columns */}
        <div className="flex items-center gap-1.5 flex-wrap">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
            <Input
              value={searchValue}
              onChange={(e) => onSearchChange?.(e.target.value)}
              placeholder={searchPlaceholder}
              className="h-8 pl-8 w-56 text-xs"
              data-testid="input-hero-search"
            />
          </div>

          {tableKey && tableColumns ? (
            <ColumnsMenu
              tableKey={tableKey}
              columns={tableColumns}
              visibility={columnVisibility ?? {}}
              onChange={(next) => onColumnVisibilityChange?.(next)}
            />
          ) : null}
        </div>
      </div>

      {/* Active filter chips row */}
      {activeChips.length > 0 ? (
        <div className="flex flex-wrap items-center gap-1.5">
          {activeChips.map((chip) => (
            <Badge
              key={`${chip.columnKey}-${chip.value}`}
              variant="outline"
              className="text-xs gap-1"
              data-testid={`chip-${chip.columnKey}-${chip.value}`}
            >
              <span className="text-muted-foreground">{chip.columnLabel}:</span>
              <span>{chip.optionLabel}</span>
              <button
                type="button"
                onClick={() => handleRemoveChip(chip.columnKey, chip.value)}
                className="ml-0.5 -mr-1 rounded-sm hover:bg-muted p-0.5"
                aria-label={`Remove ${chip.columnLabel} ${chip.optionLabel} filter`}
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      ) : null}
    </div>
  );
}

// ─── Sub-menus ──────────────────────────────────────────────────────────────

function FilterByMenu({
  columns,
  filters,
  onToggle,
}: {
  columns: HeroToolbarColumn[];
  filters: HeroFilterState;
  onToggle: (col: HeroToolbarColumn, value: string) => void;
}) {
  const totalSelected = Object.values(filters).reduce(
    (sum, v) => sum + v.length,
    0
  );

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          data-testid="button-hero-filter-by"
        >
          <Filter className="h-3.5 w-3.5" />
          Filter By
          {totalSelected > 0 ? (
            <Badge
              variant="secondary"
              className="ml-1 px-1.5 py-0 text-[10px] no-default-active-elevate"
            >
              {totalSelected}
            </Badge>
          ) : null}
          <ChevronDown className="h-3.5 w-3.5 opacity-60" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 p-0">
        <div className="max-h-96 overflow-y-auto py-1">
          {columns.map((col) => {
            const selected = filters[col.key] ?? [];
            return (
              <div key={col.key} className="px-2 py-1.5">
                <div className="px-2 pb-1 text-[10px] uppercase tracking-wider text-muted-foreground">
                  {col.label}
                </div>
                <div className="flex flex-col">
                  {col.options?.map((option) => {
                    const isSelected = selected.includes(option.value);
                    return (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => onToggle(col, option.value)}
                        className={cn(
                          "flex items-center gap-2 rounded-sm px-2 py-1.5 text-sm text-left",
                          "hover-elevate active-elevate-2",
                          isSelected && "font-medium"
                        )}
                        data-testid={`option-filter-${col.key}-${option.value}`}
                      >
                        <Check
                          className={cn(
                            "h-3.5 w-3.5 shrink-0",
                            isSelected ? "opacity-100" : "opacity-0"
                          )}
                        />
                        <span className="truncate">{option.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function GroupByMenu({
  columns,
  group,
  onChange,
}: {
  columns: HeroToolbarColumn[];
  group: HeroGroupState;
  onChange: (next: HeroGroupState) => void;
}) {
  const active = columns.find((c) => c.key === group);
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          data-testid="button-hero-group-by"
        >
          <Group className="h-3.5 w-3.5" />
          Group By
          {active ? (
            <span className="ml-1 text-muted-foreground">·</span>
          ) : null}
          {active ? (
            <span className="text-foreground">{active.label}</span>
          ) : null}
          <ChevronDown className="h-3.5 w-3.5 opacity-60" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-56 p-1">
        <button
          type="button"
          onClick={() => onChange(null)}
          className={cn(
            "flex items-center gap-2 w-full rounded-sm px-2 py-1.5 text-sm text-left",
            "hover-elevate active-elevate-2"
          )}
        >
          <Check
            className={cn(
              "h-3.5 w-3.5 shrink-0",
              group === null ? "opacity-100" : "opacity-0"
            )}
          />
          <span className="text-muted-foreground italic">None</span>
        </button>
        {columns.map((col) => {
          const isActive = col.key === group;
          return (
            <button
              key={col.key}
              type="button"
              onClick={() => onChange(col.key)}
              className={cn(
                "flex items-center gap-2 w-full rounded-sm px-2 py-1.5 text-sm text-left",
                "hover-elevate active-elevate-2",
                isActive && "font-medium"
              )}
              data-testid={`option-group-${col.key}`}
            >
              <Check
                className={cn(
                  "h-3.5 w-3.5 shrink-0",
                  isActive ? "opacity-100" : "opacity-0"
                )}
              />
              <span>{col.label}</span>
            </button>
          );
        })}
      </PopoverContent>
    </Popover>
  );
}

function SortByMenu({
  columns,
  sort,
  onPick,
  onClear,
}: {
  columns: HeroToolbarColumn[];
  sort: HeroSortState | null;
  onPick: (columnKey: string) => void;
  onClear: () => void;
}) {
  const active = columns.find((c) => c.key === sort?.columnKey);
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          data-testid="button-hero-sort-by"
        >
          <ArrowUpDown className="h-3.5 w-3.5" />
          Sort By
          {active ? (
            <span className="ml-1 inline-flex items-center gap-1">
              <span className="text-muted-foreground">·</span>
              <span className="text-foreground">{active.label}</span>
              {sort?.direction === "desc" ? (
                <ArrowDown className="h-3 w-3" />
              ) : (
                <ArrowUp className="h-3 w-3" />
              )}
            </span>
          ) : null}
          <ChevronDown className="h-3.5 w-3.5 opacity-60" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-56 p-1">
        <button
          type="button"
          onClick={onClear}
          className={cn(
            "flex items-center gap-2 w-full rounded-sm px-2 py-1.5 text-sm text-left",
            "hover-elevate active-elevate-2"
          )}
        >
          <Check
            className={cn(
              "h-3.5 w-3.5 shrink-0",
              sort === null ? "opacity-100" : "opacity-0"
            )}
          />
          <span className="text-muted-foreground italic">None</span>
        </button>
        {columns.map((col) => {
          const isActive = col.key === sort?.columnKey;
          return (
            <button
              key={col.key}
              type="button"
              onClick={() => onPick(col.key)}
              className={cn(
                "flex items-center justify-between gap-2 w-full rounded-sm px-2 py-1.5 text-sm text-left",
                "hover-elevate active-elevate-2",
                isActive && "font-medium"
              )}
              data-testid={`option-sort-${col.key}`}
            >
              <span className="flex items-center gap-2">
                <Check
                  className={cn(
                    "h-3.5 w-3.5 shrink-0",
                    isActive ? "opacity-100" : "opacity-0"
                  )}
                />
                <span>{col.label}</span>
              </span>
              {isActive ? (
                sort?.direction === "desc" ? (
                  <ArrowDown className="h-3.5 w-3.5 text-muted-foreground" />
                ) : (
                  <ArrowUp className="h-3.5 w-3.5 text-muted-foreground" />
                )
              ) : null}
            </button>
          );
        })}
      </PopoverContent>
    </Popover>
  );
}

function ColumnsMenu({
  tableKey,
  columns,
  visibility,
  onChange,
}: {
  tableKey: string;
  columns: Array<{ key: string; header: string; hideable?: boolean }>;
  visibility: Record<string, boolean>;
  onChange: (next: Record<string, boolean>) => void;
}) {
  const hideable = columns.filter((c) => c.hideable !== false);

  const handleToggle = (key: string) => {
    const current = visibility[key] ?? true;
    const next: Record<string, boolean> = { ...visibility, [key]: !current };
    onChange(next);
    try {
      window.localStorage.setItem(
        `datatable:${tableKey}:visibility`,
        JSON.stringify(next)
      );
    } catch {
      // localStorage may be unavailable — silently continue.
    }
  };

  if (hideable.length === 0) return null;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" data-testid="button-hero-columns">
          <Columns3 className="h-3.5 w-3.5" />
          Columns
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-56 p-1">
        <div className="px-2 py-1 text-[10px] uppercase tracking-wider text-muted-foreground">
          Toggle columns
        </div>
        {hideable.map((col) => {
          const isVisible = visibility[col.key] ?? true;
          return (
            <button
              key={col.key}
              type="button"
              onClick={() => handleToggle(col.key)}
              className={cn(
                "flex items-center gap-2 w-full rounded-sm px-2 py-1.5 text-sm text-left",
                "hover-elevate active-elevate-2"
              )}
              data-testid={`option-column-${col.key}`}
            >
              <Check
                className={cn(
                  "h-3.5 w-3.5 shrink-0",
                  isVisible ? "opacity-100" : "opacity-0"
                )}
              />
              <span>{col.header}</span>
            </button>
          );
        })}
      </PopoverContent>
    </Popover>
  );
}
